import { getItemRarity, type OpenRecord, resolveRarityColor } from "@loot-os/api-contract";
import { useInfiniteQuery } from "@tanstack/react-query";

import { opensInfiniteQueryOptions } from "../../../lib/queries.js";
import { useShopper } from "../../../lib/shopper.js";
import { useTenant } from "../../../lib/tenant.js";

/** How many opens a best-opens row shows. */
const ROW_LENGTH = 12;

/** One open in a best-opens row. */
export interface RankedOpen {
  open: OpenRecord;
  /** The item's rarity tier, named as the odds table and the opener's rails name it. */
  tierLabel: string;
  /** That tier's color as `#rrggbb`, with the tenant's own recoloring applied. */
  tierColor: string;
  /** The accessible name of the card that opens the pull's details. */
  detailsLabel: string;
}

/**
 * The signed-in shopper's most valuable recent opens, most valuable first; `[]` signed out. The
 * home page's "Fire pulls" and the boxes page's "Top hits" both show these.
 *
 * TODO: To be replaced with a feed of other shoppers' best opens, no route exists yet.
 */
export function useBestOpens(): RankedOpen[] {
  const { user, sessionToken } = useShopper();
  const { config } = useTenant();
  // On the session token, not the user: the opens route needs the token, which is minted after
  // the sign-in check answers, and a query run before it fails for good (no retry).
  const query = useInfiniteQuery({
    ...opensInfiniteQueryOptions(),
    enabled: Boolean(sessionToken),
  });
  // Checked here, not only by the query: a disabled query keeps the last session's cached page.
  const pages = user ? (query.data?.pages ?? []) : [];

  return pages
    .flatMap((page) => page.data)
    .sort((a, b) => b.item.value - a.item.value)
    .slice(0, ROW_LENGTH)
    .map((open) => {
      const rarity = getItemRarity(open.item.value, open.costCents);
      const { label, color } = resolveRarityColor(rarity, config.theme.rarity);
      return {
        open,
        tierLabel: label,
        tierColor: color,
        detailsLabel: `Show ${open.item.name}`,
      };
    });
}
